import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ClipboardCheck, MessageSquare } from "lucide-react";

import { OrdersFilters } from "@/components/admin/orders-filters";
import { OrdersPagination } from "@/components/admin/orders-pagination";
import { OrdersTable } from "@/components/admin/orders-table";
import { AdminShell } from "@/components/admin/admin-shell";
import { buttonVariants } from "@/components/ui/button";
import {
  STATUS_LABEL,
  type OrdersListSort,
  type OrdersListResult,
  type PackageType,
  type OrderStatus,
} from "@/lib/admin-orders-shared";
import { cn } from "@/lib/utils";

export function AdminOrdersPage() {
  const [searchParams] = useSearchParams();
  const [result, setResult] = useState<OrdersListResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Parse current URL params to pass down to filter components
  const statuses = searchParams
    .getAll("status")
    .flatMap((s) => s.split(","))
    .filter((s): s is OrderStatus => s in STATUS_LABEL);
  const packageType = (searchParams.get("package") ?? null) as PackageType | null;
  const search = searchParams.get("q") ?? "";
  const sort = (searchParams.get("sort") as OrdersListSort) || "createdAt_desc";
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    const qs = searchParams.toString();
    fetch(`/api/admin/orders${qs ? `?${qs}` : ""}`, {
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        return res.json() as Promise<OrdersListResult>;
      })
      .then((data) => {
        setResult(data);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (err.name === "AbortError") return;
        setError(err.message);
        setLoading(false);
      });

    return () => controller.abort();
  }, [searchParams]);

  return (
    <AdminShell>
      <main className="brand-bg min-h-[calc(100vh-3.5rem)]">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-10">
          <header className="flex flex-wrap items-end justify-between gap-4">
            <div className="space-y-1">
              <p className="brand-eyebrow">Admin</p>
              <h1 className="brand-heading brand-heading-md">Orders</h1>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/admin/support"
                className={cn(
                  buttonVariants({ variant: "outline", size: "sm" }),
                  "no-underline",
                )}
              >
                <MessageSquare className="size-3.5" />
                Support chat
              </Link>
              <Link
                to="/admin/qa"
                className={cn(
                  buttonVariants({ variant: "outline", size: "sm" }),
                  "no-underline",
                )}
              >
                <ClipboardCheck className="size-3.5" />
                QA test runs
              </Link>
            </div>
          </header>

          <OrdersFilters
            selectedStatuses={statuses}
            selectedPackage={packageType}
            search={search}
            sort={sort}
          />

          {error ? (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
              {error}
            </div>
          ) : (
            <div className="rounded-2xl border border-border bg-card p-2 shadow-[0_1px_0_rgba(0,0,0,0.08),0_24px_56px_-32px_rgba(0,0,0,0.2)]">
              <OrdersTable result={result} loading={loading} />
            </div>
          )}

          {result ? (
            <OrdersPagination
              page={page}
              pageCount={result.pageCount}
              total={result.total}
            />
          ) : null}
        </div>
      </main>
    </AdminShell>
  );
}
